import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { AgentsSortBy, AgentsSortOrder } from '@/pages/analytics/types';
import { parseIdListParam, serializeMultiValueParam } from '@/utils/urlParams';

import type { AgentsQuickFilter } from '../components/AgentsQuickFilterChips';

// Per spec §9 the agents-tab filters must live in the URL so refresh / back /
// shareable links preserve state. useAnalyticsFiltersState owns tab + programs
// + date range; this hook owns only agents-specific params.

const VALID_QUICK: ReadonlySet<AgentsQuickFilter> = new Set(['all', 'best', 'coaching']);
const VALID_SORT_BY: ReadonlySet<AgentsSortBy> = new Set(['avg_score', 'call_count']);
const VALID_SORT_ORDER: ReadonlySet<AgentsSortOrder> = new Set(['asc', 'desc']);
const ALLOWED_PAGE_SIZES = new Set([20, 50, 100]);
const DEFAULT_PAGE_SIZE = 20;

const parseInt10 = (raw: string | null, fallback: number): number => {
  const n = raw ? parseInt(raw, 10) : NaN;
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

const parseQuickFilter = (raw: string | null): AgentsQuickFilter => {
  const value = raw as AgentsQuickFilter | null;
  return value && VALID_QUICK.has(value) ? value : 'all';
};

const parseSortBy = (raw: string | null): AgentsSortBy => {
  const value = raw as AgentsSortBy | null;
  return value && VALID_SORT_BY.has(value) ? value : 'avg_score';
};

const parseSortOrder = (raw: string | null): AgentsSortOrder => {
  const value = raw as AgentsSortOrder | null;
  return value && VALID_SORT_ORDER.has(value) ? value : 'desc';
};

const parsePageSize = (raw: string | null): number => {
  const pageSize = parseInt10(raw, DEFAULT_PAGE_SIZE);
  return ALLOWED_PAGE_SIZES.has(pageSize) ? pageSize : DEFAULT_PAGE_SIZE;
};

export const useAgentsTabFiltersState = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const agentIds = useMemo(() => parseIdListParam(searchParams.get('agent_ids')), [searchParams]);

  const quickFilter = parseQuickFilter(searchParams.get('quick'));
  const sortBy = parseSortBy(searchParams.get('sort_by'));
  const sortOrder = parseSortOrder(searchParams.get('sort_order'));
  const page = parseInt10(searchParams.get('page'), 1);
  const pageSize = parsePageSize(searchParams.get('page_size'));

  const updateSearchParams = useCallback(
    (updates: Record<string, string | undefined>) => {
      setSearchParams(
        prev => {
          const params = new URLSearchParams(prev);
          Object.entries(updates).forEach(([k, v]) => {
            if (v == null || v === '') params.delete(k);
            else params.set(k, v);
          });
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setAgentIds = useCallback(
    (next: string[]) => {
      updateSearchParams({ agent_ids: serializeMultiValueParam(next), page: undefined });
    },
    [updateSearchParams],
  );

  const setQuickFilter = useCallback(
    (next: AgentsQuickFilter) => {
      updateSearchParams({ quick: next === 'all' ? undefined : next, page: undefined });
    },
    [updateSearchParams],
  );

  const setSort = useCallback(
    (next: { sortBy: AgentsSortBy; sortOrder: AgentsSortOrder }) => {
      updateSearchParams({
        sort_by: next.sortBy === 'avg_score' ? undefined : next.sortBy,
        sort_order: next.sortOrder === 'desc' ? undefined : next.sortOrder,
        // Reset to page 1 when sort changes — without this users land on a stale
        // page index of a freshly-sorted list.
        page: undefined,
      });
    },
    [updateSearchParams],
  );

  const setPage = useCallback(
    (next: number) => updateSearchParams({ page: next === 1 ? undefined : String(next) }),
    [updateSearchParams],
  );

  const setPageSize = useCallback(
    (next: number) => {
      const normalized = ALLOWED_PAGE_SIZES.has(next) ? next : DEFAULT_PAGE_SIZE;
      updateSearchParams({
        page_size: normalized === DEFAULT_PAGE_SIZE ? undefined : String(normalized),
        page: undefined,
      });
    },
    [updateSearchParams],
  );

  return {
    agentIds,
    setAgentIds,
    quickFilter,
    setQuickFilter,
    sortBy,
    sortOrder,
    setSort,
    page,
    setPage,
    pageSize,
    setPageSize,
  };
};
